import { defineStore } from 'pinia';
import { useDepartmentStore } from '@/stores/department';
import { isAfter, isBefore, endOfDay, subDays, addDays } from 'date-fns';
import { format } from '@/utils/date-fns';
import { toDate } from 'date-fns-tz';
import { get } from 'lodash';
import { useInstitutionStore } from '@/stores/institution';
import { useProfilesStore } from '@/stores/profiles';
import api from '@/api';
import { ShiftFragment, ProcessedShiftFragment } from '@/graphql/custom-types';

type FilterType = 'later' | 'now';

interface IShiftUpdateData {
  cancel: boolean;
  dates?: unknown;
}

interface IShiftDeleteData {
  shiftId: string;
  dates?: unknown;
}

interface IWorkScheduleState {
  all: ShiftFragment[];
  workingNow: ProcessedShiftFragment[];
  comingLater: ProcessedShiftFragment[];
  isLoading: boolean;
  type: string;
  selectedShift: ShiftFragment | ProcessedShiftFragment | Record<string, never>;
  editAction: string;
  replaceWithEmployeeId: string | null;
}

const getFilteredShifts = (
  schedule: ShiftFragment[],
  filter: FilterType
): ProcessedShiftFragment[] => {
  return schedule
    .filter((shift) => {
      if (filter === 'later') {
        if (
          isAfter(new Date(shift.start), new Date()) &&
          isBefore(new Date(shift.start), endOfDay(new Date()))
        )
          return true;
        return false;
      }

      if (isAfter(new Date(), new Date(shift.end))) return false;
      if (isAfter(new Date(shift.start), new Date())) return false;
      return true;
    })
    .map((shift) => {
      return {
        ...shift,
        start: toDate(shift.start, { timeZone: 'Europe/Copenhagen' }),
        end: toDate(shift.end, { timeZone: 'Europe/Copenhagen' }),
      };
    });
};

const getRange = () => {
  const institutionStore = useInstitutionStore();
  const departmentStore = useDepartmentStore();

  const matchingDepartment = institutionStore.settings.shift?.find(
    ({ departmentId }) =>
      departmentId && departmentId.toString() == departmentStore.id.toString()
  );
  const now = new Date();

  // Subtract 1 because the core admin setting is not 0 based but 1 should mean 1 days forward.
  const daysForward =
    Number(
      get(
        matchingDepartment,
        'daysForward',
        departmentStore.settings?.shift?.daysForward ?? 1
      )
    ) - 1;

  return {
    from: subDays(now, daysForward),
    to: addDays(now, daysForward),
  };
};

export const useWorkScheduleStore = defineStore('workSchedule', {
  state: (): IWorkScheduleState => ({
    all: [],
    workingNow: [],
    comingLater: [],
    isLoading: false,
    type: '',
    selectedShift: {},
    editAction: '',
    replaceWithEmployeeId: null,
  }),
  actions: {
    async getWorkSchedules() {
      const departmentStore = useDepartmentStore();
      const range = getRange();

      const shifts = await api.workSchedule.getShifts({
        filter: {
          departments: [departmentStore.id],
        },
        dateFrom: format(range.from, 'yyyy-MM-dd'),
        dateTo: format(range.to, 'yyyy-MM-dd'),
        timezone: 'Europe/Copenhagen',
      });

      this.all = shifts;
      this.comingLater = getFilteredShifts(shifts, 'later');
      this.workingNow = getFilteredShifts(shifts, 'now');

      if (this.selectedShift?.id) {
        const updatedShift = shifts.find((shift) => {
          return shift?.id === this.selectedShift?.id;
        });

        this.selectedShift = updatedShift ?? {};
      }
    },
    async shiftUpdate(shiftData: IShiftUpdateData) {
      const profilesStore = useProfilesStore();

      try {
        await api.workSchedule.shiftUpdate({
          id: this.selectedShift.id,
          employeeId:
            this.replaceWithEmployeeId || this.selectedShift.employee?.id,
          cancelled: shiftData.cancel,
        });

        this.replaceWithEmployeeId = null;

        await this.getWorkSchedules();

        await profilesStore.setPinValidation({
          validating: false,
          validPin: undefined,
        });
      } catch {
        this.replaceWithEmployeeId = null;
        profilesStore.setPinValidation({
          validating: false,
          validPin: undefined,
        });
        throw new Error('Error updating shift');
      }
    },
    async shiftDelete(shiftData: IShiftDeleteData) {
      const profilesStore = useProfilesStore();

      try {
        await api.workSchedule.shiftDelete(shiftData.shiftId);

        // Deleted shift no longer exists after the refetch, so clear it explicitly.
        this.selectedShift = {};
        await this.getWorkSchedules();

        // validPin: false leaves the PIN overlay stuck in its "wrong PIN" state for the next shift.
        await profilesStore.setPinValidation({
          validating: false,
          validPin: undefined,
        });
      } catch {
        profilesStore.setPinValidation({
          validating: false,
          validPin: undefined,
        });
        throw new Error('Error deleting shift');
      }
    },
    setSelectedShift(shift: ShiftFragment | ProcessedShiftFragment) {
      this.selectedShift = shift;
    },
    setReplaceWithEmployeeId(id: string | null) {
      this.replaceWithEmployeeId = id;
    },
    setEditAction(action: string) {
      this.editAction = action;
    },
  },
});
